import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useState, type Dispatch, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { apiClient, type AuthTokensDto } from '../../../api';
import { tokens } from '../../../design/tokens';
import { Routes } from '../../../routes/routes';
import { useAuthStore } from '../../../stores';
import { CodeInput } from '../_components/CodeInput';
import { StepHeading } from '../_components/StepHeading';
import { mapLoginError } from '../loginErrorMap';
import type { LoginAction, LoginState } from '../loginStateMachine';

const CODE_LENGTH = 6;

export type TotpStepProps = {
  state: LoginState;
  dispatch: Dispatch<LoginAction>;
};

type AuthenticatorHintProps = {
  email: string;
  title: string;
  timeLabel: string;
};

const AuthenticatorHint = ({ email, title, timeLabel }: AuthenticatorHintProps): ReactElement => (
  <Box
    sx={{
      mb: '24px',
      padding: '14px 16px',
      borderRadius: '14px',
      background: tokens.brand.subtle,
      border: `1px solid ${tokens.border.default}`,
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontWeight: 600, color: tokens.text.primary, fontSize: 13 }}>
        {title}
      </Typography>
      <Typography
        sx={{
          fontSize: 11,
          color: tokens.text.tertiary,
          marginTop: '2px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {email}
      </Typography>
    </Box>
    <Typography sx={{ fontSize: 11, color: tokens.text.tertiary, fontFamily: tokens.font.mono }}>
      {timeLabel}
    </Typography>
  </Box>
);

export const TotpStep = ({ state, dispatch }: TotpStepProps): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authStore = useAuthStore();
  const [code, setCode] = useState('');

  const submitTotp = async (value: string): Promise<void> => {
    if (!state.tempToken) {
      dispatch({ type: 'submit_failed', errorKey: 'auth.errors.unknown' });
      return;
    }
    dispatch({ type: 'submit_started' });
    try {
      const response = await apiClient.post<AuthTokensDto>('/auth/totp/challenge', {
        tempToken: state.tempToken,
        code: value,
      });
      dispatch({ type: 'totp_complete' });
      authStore.completeLogin(response.data);
      navigate(Routes.App.Dashboard, { replace: true });
    } catch (err) {
      const mapped = mapLoginError(err, 'totp');
      dispatch({ type: 'submit_failed', errorKey: mapped.key });
      setCode('');
    }
  };

  return (
    <Box>
      <StepHeading
        eyebrow={t('auth.login.totp.step')}
        title={t('auth.login.totp.title')}
        subtitle={t('auth.login.totp.subtitle')}
      />
      <AuthenticatorHint
        email={state.email}
        title={t('auth.login.totp.authAppName')}
        timeLabel={t('auth.login.totp.timeRemaining', { seconds: 30 })}
      />
      <CodeInput
        length={CODE_LENGTH}
        value={code}
        onChange={setCode}
        onComplete={submitTotp}
        disabled={state.isSubmitting}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        errored={Boolean(state.errorKey)}
        ariaLabel={t('auth.login.totp.title')}
        testId="totp-code"
      />
      {state.errorKey && (
        <Alert severity="error" sx={{ mt: '16px' }} data-testid="totp-error">
          {t(state.errorKey)}
        </Alert>
      )}
      <Button
        fullWidth
        variant="contained"
        size="large"
        type="button"
        disabled={state.isSubmitting || code.length !== CODE_LENGTH}
        onClick={() => void submitTotp(code)}
        sx={{ mt: '20px', height: 52, borderRadius: '14px' }}
      >
        {t('auth.login.totp.cta')}
      </Button>
      <Box sx={{ mt: '18px', textAlign: 'center' }}>
        <Typography sx={{ fontSize: 12, color: tokens.text.tertiary }}>
          {t('auth.login.totp.helpRow')}
        </Typography>
      </Box>
    </Box>
  );
};
